import { SubscriptionPlanType } from 'src/app/model/subscription-plan-type.enum';

/** Subscription plan values seen on profile / dashboard payloads. */
export type SubscriptionPlanTypeValue = SubscriptionPlanType | string | number | null | undefined;

/** Permission that reveals Voice pages (Phone Numbers, Call List, and related routes). */
export const AI_STUDIO_VOICE_PERMISSION = 'ai.studio.agent.voice';

/** Subscription payload fields used to lock Voice actions. */
export interface VoiceAgentSubscriptionDetails {
  planType?: SubscriptionPlanTypeValue;
  isVoiceAgentAvailable?: boolean | null;
}

/**
 * Detects free / free-trial subscription values from numeric or string plan types.
 * @param planType tenant subscription plan value from profile payload
 * @returns true when plan matches free / free-trial variants
 */
export function isFreeTrialSubscription(planType: SubscriptionPlanTypeValue): boolean {
  if (planType === null || planType === undefined) {
    return false;
  }
  if (typeof planType === 'number') {
    return planType === SubscriptionPlanType.Free;
  }
  const normalized = String(planType).trim().toLowerCase().split(/[\s-]+/).join('_');
  return (
    normalized === 'free' ||
    normalized === 'free_trial' ||
    normalized === 'freetrial' ||
    normalized === String(SubscriptionPlanType.Free)
  );
}

/**
 * Whether Voice is available on the current subscription.
 * Missing `isVoiceAgentAvailable` is fail-open (treated as available).
 *
 * @param subscriptionDetails user-profile subscriptionDetails payload
 * @returns false only when `isVoiceAgentAvailable` is explicitly false
 */
export function isVoiceAgentAvailableOnSubscription(
  subscriptionDetails: VoiceAgentSubscriptionDetails | null | undefined
): boolean {
  return subscriptionDetails?.isVoiceAgentAvailable !== false;
}

/**
 * Whether Voice actions (tabs, create type, call/phone buttons) should be disabled.
 * Locked only when `isVoiceAgentAvailable` is explicitly false. Quota and permission
 * gates live elsewhere.
 *
 * @param subscriptionDetails user-profile subscriptionDetails payload
 * @returns true when Voice actions should be disabled
 */
export function isVoiceAgentActionLocked(
  subscriptionDetails: VoiceAgentSubscriptionDetails | null | undefined
): boolean {
  return !isVoiceAgentAvailableOnSubscription(subscriptionDetails);
}

/**
 * Whether an agent type name is the Voice agent type.
 * @param name agent type name from API
 * @returns true when name is Voice
 */
export function isVoiceAgentTypeName(name: string | null | undefined): boolean {
  return String(name ?? '').trim().toLowerCase() === 'voice';
}
